import { useState, useEffect, isValidElement } from 'react';
import { Redirect } from 'react-router';
import { withRouter } from 'react-router';

import useAuthentication from 'hooks/useAuthentication';

import Loader from 'components/Loader';
import Tokens from 'providers/Tokens';
import Logger from 'utilities/logger';

const withPermissionProtection = args => Component => {
  let {
    permissions: permissionsRequired,
    fallback: Fallback,
    fallbackRoute,
    requireAccessToken = true,
  } = args || {};

  const C = props => {
    const {
      permissions: availablePermissions,
      requestToken,
    } = useAuthentication();
    const [state, setState] = useState({ loading: true, success: false });
    const logger = Logger('permissions protection');

    if (typeof args === 'string') permissionsRequired = [args];
    if (Array.isArray(args)) permissionsRequired = args;
    if (typeof permissionsRequired === 'string')
      permissionsRequired = [permissionsRequired];

    useEffect(() => {
      const missingTokens = [];
      const missingPermissions = [];
      const controller = new AbortController();
      const signal = controller.signal;
      const update = data => {
        if (!signal.aborted) {
          setState(old => ({ ...old, ...data }));
        }
      };
      for (let index = 0; index < permissionsRequired.length; index++) {
        const required = permissionsRequired[index];
        if (Array.isArray(required)) {
          // one of the scopes in the array is needed for access,
          // but *does not need* all of them
          const found = required.filter(rp =>
            availablePermissions.includes(rp),
          );
          // User does not have any of the scopes requested
          if (found.length === 0) {
            logger.debug('Require any', required);
            logger.error('Permission required');
            missingPermissions.push(required);
            break;
          }
          if (!requireAccessToken) continue;
          // Fetch all available tokens, user might use them *soon* in anycase
          found.forEach(permission => {
            if (!Tokens.get(`token_${permission}`))
              missingTokens.push(permission);
          });
        } else {
          if (!availablePermissions.includes(required)) {
            logger.debug('Required', required);
            logger.error('Permission required');
            missingPermissions.push(required);
            break;
          }
          if (!requireAccessToken) continue;
          if (!Tokens.get(`token_${required}`)) {
            missingTokens.push(required);
          }
        }
      }

      if (missingPermissions.length > 0) {
        logger.debug('missingPermissions', missingPermissions);
        update({ loading: false, success: false });
      } else if (missingTokens.length > 0) {
        logger.debug(`Fetching missing tokens: ${missingTokens.join(', ')}`);
        Promise.all(missingTokens.map(scope => requestToken(scope)))
          .then(() => {
            logger.debug('Requested tokens fetched successfully');
            update({ loading: false, success: true });
            return;
          })
          .catch(err => {
            update({ loading: false, success: false });
            logger.error(err);
            return;
          });
      } else {
        logger.debug('Access Granted');
        update({ loading: false, success: true });
      }

      return () => {
        controller.abort(); // abort if the component is unmounted
      };
    }, []);

    if (state.loading) {
      return <Loader />;
    }

    if (!state.success) {
      logger.debug(
        `Redirecting to ${
          Fallback ? 'Fallback' : fallbackRoute ? 'fallbackRoute' : '/'
        }`,
      );
      if (Fallback !== undefined && isValidElement(<Fallback />)) {
        return <Fallback />;
      }
      if (typeof fallbackRoute === 'string') {
        return <Redirect push exact to={fallbackRoute} />;
      }
      return <Redirect push exact to="/" />;
    }

    return <Component {...props} />;
  };

  const name = Component.displayName || Component.name;
  C.displayName = `withPermissionProtection(${name})`;

  return withRouter(C);
};

export default withPermissionProtection;
